import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";

// Handicap System numbers used to be hardcoded to one society (society_id
// 7) in the backend — see societySettings.js. This page is what makes them
// genuinely configurable per-society, as the User Manual describes.
export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/settings")
      .then(res => setSettings(res.data))
      .catch(() => setToast("Failed to load settings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const set = (field, value) => setSettings(s => ({ ...s, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/settings", settings);
      setSettings(res.data);
      showToast("Settings saved");
    } catch (e) {
      showToast("Failed: " + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <Layout><div className="p-6 text-gray-500">Loading settings...</div></Layout>;
  }

  const numField = (label, field, width = "w-20") => (
    <div className="flex items-center gap-2">
      <label className="text-sm text-gray-700 w-40">{label}</label>
      <input type="number" value={settings[field]} onChange={e => set(field, parseInt(e.target.value) || 0)}
        className={`${width} border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500`} />
    </div>
  );

  return (
    <Layout>
      <div className="p-6 max-w-2xl">
        <h1 className="text-2xl font-bold text-green-700 mb-1">⚙️ Changeable Settings</h1>
        <p className="text-sm text-gray-500 mb-6">Adjust how Golf Society Manager applies rules for your society.</p>

        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Handicap System</h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.handicap_system_enabled}
                onChange={e => set("handicap_system_enabled", e.target.checked)}
                className="w-4 h-4 accent-green-600" />
              <span className="text-sm text-gray-700">Enabled</span>
            </label>
          </div>

          {settings.handicap_system_enabled ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                New members are capped at their club handicap or the number below, whichever is lower.
                No player's handicap can ever be edited above the maximum. After archiving a round, each
                player's handicap adjusts automatically based on points scored.
              </p>
              <div className="space-y-2">
                {numField("New member cap", "new_member_cap")}
                {numField("Maximum handicap", "max_handicap")}
              </div>

              <h3 className="text-sm font-semibold text-gray-700 mt-4 mb-2">Points bands (adjustment after each round)</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="w-40">Points up to</span><span className="w-20">Handicap change</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" value={settings.band1_max} onChange={e => set("band1_max", parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                  <input type="number" value={settings.band1_delta} onChange={e => set("band1_delta", parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" value={settings.band2_max} onChange={e => set("band2_max", parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                  <input type="number" value={settings.band2_delta} onChange={e => set("band2_delta", parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" value={settings.band3_max} onChange={e => set("band3_max", parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                  <input type="number" value={settings.band3_delta} onChange={e => set("band3_delta", parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" value={settings.band4_max} onChange={e => set("band4_max", parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                  <input type="number" value={settings.band4_delta} onChange={e => set("band4_delta", parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-20 text-sm text-gray-500">above</span>
                  <input type="number" value={settings.band5_delta} onChange={e => set("band5_delta", parseInt(e.target.value) || 0)} className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                </div>
              </div>

              <div className="mt-3">
                {numField("Winner's extra reduction", "winner_bonus")}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Switch this on to configure new-member caps, a maximum handicap, and automatic post-round adjustment.</p>
          )}
        </div>

        {settings.show_tuesday_settings && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Tuesday Golf</h2>
            <p className="text-sm text-gray-500 mb-3">Only shown for Pin High — Tuesday Golf isn't a general Golf Society Manager feature.</p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 w-40">Organiser PIN</label>
              <input type="text" value={settings.tuesday_organiser_pin || ""} onChange={e => set("tuesday_organiser_pin", e.target.value)}
                className="w-40 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <p className="text-xs text-gray-500 mt-2">Required to change the date or tee time on tues.golfsocietymanager.com. Anyone with the sign-up PIN can still add or remove themselves from a slot.</p>
          </div>
        )}

        <button onClick={save} disabled={saving}
          className={`font-semibold py-2 px-6 rounded-lg text-white text-sm ${saving ? "bg-gray-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
          {saving ? "Saving..." : "Save Settings"}
        </button>

        {toast && <div className="fixed bottom-6 right-6 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm">{toast}</div>}
      </div>
    </Layout>
  );
}
